/**
 * BD Quick Cart - Enhanced cart drawer functionality
 * Handles cart operations, progress bar, badges, and upsells
 */

class BDQuickCart {
  constructor() {
    this.cart = null;
    this.isOpen = false;
    this.isLoading = false;
    this.freeGiftThreshold = parseFloat(document.querySelector('[data-free-gift-threshold]')?.dataset.freeGiftThreshold) || 50;
    this.freeGiftProduct = document.querySelector('[data-free-gift-product]')?.dataset.freeGiftProduct || null;
    this.autoAddFreeGift = document.querySelector('[data-bd-quick-cart]')?.dataset.autoAddFreeGift !== 'false';
    this.freeGiftAdded = false;
    this.isRemovingFreeGift = false;

    this.selectors = {
      cart: '[data-bd-quick-cart]',
      overlay: '[data-overlay]',
      closeBtn: '[data-close-icon]',
      items: '[data-items]',
      empty: '[data-empty]',
      footer: '[data-footer]',
      subtotal: '[data-subtotal]',
      qtyDropdown: '[data-qty-dropdown]',
      qtyTrigger: '[data-qty-trigger]',
      qtyOptions: '[data-qty-options]',
      qtyOption: '[data-qty-option]',
      qtyValue: '[data-qty-value]',

      addUpsell: '[data-add-upsell]',
      variantSelect: '[data-upsell-variant-select]',
      checkoutBtn: '[data-checkout-button]',
      progressFill: '.bd-quick-cart__progress-fill',
      progressText: '.bd-quick-cart__progress-text',
      giftTemplate: '#bd-free-gift-template'
    };

    this.init();
  }

  init() {
    this.bindEvents();
    this.loadCart();
  }

  bindEvents() {
    // Open cart triggers
    document.addEventListener('click', (e) => {
      const target = e.target.matches('[data-open-cart]') ? e.target : e.target.closest('[data-open-cart]');
      if (target) {
        e.preventDefault();

        // If button also has data-add-to-cart, handle add to cart first
        if (target.hasAttribute('data-add-to-cart') && target.type === 'button') {
          this.handleAddToCartAndOpen(target);
        } else {
          this.open();
        }
      }
    });

    // Close cart
    document.addEventListener('click', (e) => {
      if (e.target.matches(this.selectors.overlay) || e.target.matches(this.selectors.closeBtn) || e.target.closest(this.selectors.closeBtn)) {
        this.close();
      }
    });

    // Quantity dropdown toggle
    document.addEventListener('click', (e) => {
      if (e.target.matches(this.selectors.qtyTrigger) || e.target.closest(this.selectors.qtyTrigger)) {
        e.preventDefault();
        const trigger = e.target.closest(this.selectors.qtyTrigger);
        const dropdown = trigger.closest(this.selectors.qtyDropdown);
        this.toggleQuantityDropdown(dropdown);
      }

      // Close dropdowns when clicking outside
      if (!e.target.closest(this.selectors.qtyDropdown)) {
        this.closeAllQuantityDropdowns();
      }
    });

    // Quantity option selection
    document.addEventListener('click', (e) => {
      if (e.target.matches(this.selectors.qtyOption) || e.target.closest(this.selectors.qtyOption)) {
        e.preventDefault();
        const option = e.target.closest(this.selectors.qtyOption);
        const dropdown = option.closest(this.selectors.qtyDropdown);
        const itemId = dropdown.dataset.itemId;
        const newQty = parseInt(option.dataset.qtyOption) || 0;
        this.selectQuantityOption(dropdown, option, newQty);
        this.setQuantity(itemId, newQty);
      }
    });


    // ADD: Single variant dropdown → keeps the existing upsell button and only updates its attributes
    document.addEventListener('change', (e) => {
      if (e.target.matches(this.selectors.variantSelect)) {
        this.updateUpsellButtonForSelect(e.target);
      }
    });


    // Add upsell
    document.addEventListener('click', (e) => {
      if (e.target.matches(this.selectors.addUpsell) || e.target.closest(this.selectors.addUpsell)) {
        const btn = e.target.closest(this.selectors.addUpsell);
        const variantId = btn.dataset.variantId;
        this.addUpsell(variantId);
      }
    });

    // Upsell carousel scroll buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-scroll-prev]')) {
        e.preventDefault();
        this.scrollUpsells('prev');
      }
      if (e.target.closest('[data-scroll-next]')) {
        e.preventDefault();
        this.scrollUpsells('next');
      }
    });

    // Remove Gift buttons
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.remove-gift');
      if (removeBtn) {
        e.preventDefault();
        const variantId = removeBtn.dataset.itemId;
        this.removeItem(variantId);
      }
    });

    // Touch/swipe support for upsell carousel
    this.initUpsellCarouselTouch();

    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Cart update events
    document.addEventListener('cart:updated', (e) => {
      this.cart = e.detail;
      this.render();
      this.updateProgress();
    });

    // Add to cart events
    document.addEventListener('cart:item-added', () => {
      this.loadCart();
      this.open();
      this.refreshCartSection();
    });

    // ADD: Initialize defaults on first load + observe dynamic changes
    this.initializeVariantSelectDefaults();
    this.observeUpsellsForVariantSelects();
  }

  async refreshCartSection() {
    try {
      const res = await fetch(`/cart?sections=bd-quick-cart_section`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const html = data["bd-quick-cart_section"];
      const container = document.querySelector("#shopify-section-bd-quick-cart_section");
      
      if (container && typeof html === 'string') {
        container.innerHTML = html;
        // re-init anything that relies on fresh DOM
        this.initializeVariantSelectDefaults(container);
        this.observeUpsellsForVariantSelects();
        this.updateScrollButtons();
        if (this.isOpen) this.open();
      }
    } catch (e) {
      console.error('Error refreshing cart section:', e);
    }
  }

  // Returns an object with Feature_1_* and Feature_2_* taken from the DOM
  collectTopTwoFeatureProps(fromEl) {
    // Scope search near the clicked button if possible
    const root = fromEl?.closest('[data-section-type]') || document;

    const items = root.querySelectorAll('.product-feature-list__items .product-feature-list__item');
    const props = {};
    let idx = 0;

    for (const item of items) {
      if (idx >= 2) break;
      const textEl = item.querySelector('.product-feature-list__text');
      const imgEl = item.querySelector('.product-feature-list__icon img');

      const text = (textEl?.textContent || '').trim();
      const icon = imgEl?.getAttribute('src') || '';
      const alt = imgEl?.getAttribute('alt') || '';

      const i = idx + 1;
      if (text) props[`Feature_${i}_text`] = text;
      if (icon) props[`Feature_${i}_icon`] = icon;
      if (alt) props[`Feature_${i}_icon_alt`] = alt;

      idx++;
    }
    return props;
  }


  async loadCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      this.cart = await response.json();

      // Fetch product metafields for each cart item
      await this.enrichCartWithMetafields();

      this.render();

      // Skip progress update and free gift check when removing free gifts to prevent loops
      if (!this.isRemovingFreeGift) {
        this.updateProgress();
        this.checkFreeGift();
      }

      this.updateCartIcon();
    } catch (error) {
      console.error('Error loading cart:', error);
      // Gracefully handle cart loading failure
      this.cart = { items: [], total_price: 0, item_count: 0 };
      this.render();
    }
  }

  async enrichCartWithMetafields() {
    if (!this.cart?.items?.length) return;

    // Unieke handles
    const handles = [...new Set(
      this.cart.items.map(i => i.handle || i.product_handle).filter(Boolean)
    )];

    // Producten ophalen
    const productsByHandle = {};
    await Promise.all(handles.map(async (handle) => {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (res.ok) productsByHandle[handle] = await res.json();
      } catch (e) {
        console.error('Product fetch error for', handle, e);
      }
    }));

    // Items verrijken met variant price & compare_at_price
    this.cart.items = this.cart.items.map(item => {
      const handle = item.handle || item.product_handle;
      const product = productsByHandle[handle];
      const variant = product?.variants?.find(v => v.id === item.variant_id);

      const compareAt = Number(variant?.compare_at_price || 0);
      const variantPrice = Number(variant?.price ?? item.price ?? 0);

      return {
        ...item,
        _variant_price: variantPrice,
        _compare_at_price: compareAt
        // Let op: GEEN properties schrijven hier
      };
    });
  }

  async updateQuantity(itemId, change) {
    if (this.isLoading) return;

    const currentItem = this.cart.items.find(item => item.variant_id.toString() === itemId.toString());
    if (!currentItem) return;

    const newQuantity = Math.max(0, currentItem.quantity + change);
    await this.setQuantity(itemId, newQuantity);
  }

  async setQuantity(itemId, quantity) {
    if (this.isLoading) return;

    this.setLoading(true);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: itemId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      this.cart = await response.json();

      // Fetch product metafields for updated cart
      await this.enrichCartWithMetafields();

      this.render();
      this.updateProgress();
      this.checkFreeGift();
      this.updateCartIcon();

      // Dispatch cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: this.cart
      }));

    } catch (error) {
      console.error('Fehler beim Verändern der Anzahl:', error);
      this.showNotification('Fehler beim Verändern der Anzahl', 'error');
    } finally {
      this.setLoading(false);
    }
  }

  async removeItem(itemId) {
    await this.setQuantity(itemId, 0);
  }

  async refreshCartSection() {
    try {
      const res = await fetch('/cart?sections=bd-quick-cart_section');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const html = data['bd-quick-cart_section'];
      const container = document.querySelector('#shopify-section-bd-quick-cart_section');

      if (container && typeof html === 'string') {
        container.innerHTML = html;
      }
    } catch (e) {
      console.error('Error refreshing cart section:', e);
    }
  }

  async addUpsell(variantId) {
    if (this.isLoading) return;

    this.setLoading(true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await this.loadCart();
      await this.refreshCartSection();

      // Show success feedback
      this.showNotification('Product added to cart!');

    } catch (error) {
      console.error('Error adding upsell:', error);
      this.showNotification('Error adding product', 'error');
    } finally {
      this.setLoading(false);
    }
  }


  async addFreeGift() {
    if (!this.freeGiftProduct || this.isLoading) return;

    // Check if free gift is already in cart (comprehensive check)
    const freeGiftItems = this.cart.items.filter(item =>
      (item.properties && item.properties._free_gift === 'true') ||
      item.product_handle === this.freeGiftProduct ||
      item.variant_id === parseInt(this.freeGiftProduct)
    );

    // If we already have any free gift items, don't add another
    if (freeGiftItems.length > 0) {
      // If we have more than one, remove extras
      if (freeGiftItems.length > 1) {
        for (let i = 1; i < freeGiftItems.length; i++) {
          const itemKey = freeGiftItems[i].key || freeGiftItems[i].variant_id;
          await this.setQuantity(itemKey, 0);
        }
      }
      return;
    }

    try {
      // Get the free gift product variant
      const productResponse = await fetch(`/products/${this.freeGiftProduct}.js`);

      if (!productResponse.ok) {
        throw new Error(`HTTP error! status: ${productResponse.status}`);
      }

      const product = await productResponse.json();

      if (product && product.variants && product.variants.length > 0) {
        const variantId = product.variants[0].id;

        // Add with price override to 0
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: {
              '_free_gift': 'true'
            }
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        await this.loadCart();
        await this.refreshCartSection();
        // this.showNotification('Free gift added to your cart! 🎁');
      }
    } catch (error) {
      console.error('Error adding free gift:', error);
      this.showNotification('Error adding free gift', 'error');
    } 
  }

  checkFreeGift() {
    if (!this.freeGiftProduct) return;

    // Use cart total excluding free gifts for accurate threshold calculation
    const cartTotal = this.getCartTotalExcludingFreeGifts() / 100; // Convert from cents
    const freeGiftItems = this.cart.items.filter(item =>
      item.properties && item.properties._free_gift === 'true'
    );

    const hasGift = freeGiftItems.length > 0;

    if (cartTotal >= this.freeGiftThreshold) {
      if (!hasGift) {
        // Add free gift when threshold is met and no gift exists
        this.addFreeGift();
      } else if (freeGiftItems.length > 1) {
        // If multiple free gifts exist, keep only one
        this.isRemovingFreeGift = true;

        // Keep the first one, schedule removal of others
        for (let i = 1; i < freeGiftItems.length; i++) {
          const itemKey = freeGiftItems[i].key || freeGiftItems[i].variant_id;
          this.setQuantity(itemKey, 0);
        }

        this.isRemovingFreeGift = false;
      }
    } else if (cartTotal < this.freeGiftThreshold && hasGift) {
      // Remove free gift when threshold is not met
      if (!this.isRemovingFreeGift) {
        this.handleFreeGiftRemoval();
      }
    }
  }

  updateProgress() {
    const progressFill = document.querySelector(this.selectors.progressFill);
    const progressText = document.querySelector(this.selectors.progressText);

    if (!progressFill || !progressText) return;

    // Calculate cart total excluding free gifts
    const cartTotal = this.getCartTotalExcludingFreeGifts() / 100; // Convert from cents
    const remaining = Math.max(0, this.freeGiftThreshold - cartTotal);
    const percentage = Math.min(100, (cartTotal / this.freeGiftThreshold) * 100);

    progressFill.style.width = `${percentage}%`;

    // Check if cart total is below threshold (including when cart is empty)
    if (cartTotal < this.freeGiftThreshold) {
      // Get text settings from data attributes or use defaults
      const container = document.querySelector('[data-bd-quick-cart]');
      const prefix = container?.dataset.progressPrefix || 'Noch';
      const suffix = container?.dataset.progressSuffix || 'für gratis Geschenk';

      progressText.innerHTML = `<span>${prefix} €${remaining.toFixed(2)} ${suffix}</span>`;
      // Remove free gift if cart total is below threshold (only if not already removing)
      if (!this.isRemovingFreeGift) {
        this.handleFreeGiftRemoval();
      }
    } else {
      // Get unlocked text from data attribute or use default
      const container = document.querySelector('[data-bd-quick-cart]');
      const unlockedText = container?.dataset.progressUnlocked || 'Gratis Geschenk freigeschaltet!';

      progressText.innerHTML = `<span class="bd-quick-cart__gift-unlocked p-xs">${unlockedText}</span>`;
      // Auto-add free gift if enabled and not already added
      this.handleFreeGiftAutoAdd();
    }
  }

  // Calculate cart total excluding free gift items
  getCartTotalExcludingFreeGifts() {
    if (!this.cart?.items) return 0;

    return this.cart.items.reduce((total, item) => {
      // Skip items marked as free gifts
      if (item.properties && item.properties._free_gift === 'true') {
        return total;
      }
      return total + (item.line_price || 0);
    }, 0);
  }

  // Handle automatic free gift removal when threshold not met
  async handleFreeGiftRemoval() {
    if (!this.cart?.items || this.isRemovingFreeGift) return;

    // Find free gift items in cart
    const freeGiftItems = this.cart.items.filter(item =>
      item.properties && item.properties._free_gift === 'true'
    );

    if (freeGiftItems.length === 0) return;

    this.isRemovingFreeGift = true;

    try {
      // Remove all free gift items
      for (const freeGiftItem of freeGiftItems) {
        // Use the line item key (which could be variant_id or key depending on cart structure)
        const itemKey = freeGiftItem.key || freeGiftItem.variant_id;

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: itemKey,
            quantity: 0
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Cart API Error:', response.status, errorText);
          throw new Error(`Failed to remove free gift: ${response.status} - ${errorText}`);
        }
      }

      this.freeGiftAdded = false;

      // Update cart state without triggering progress update
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        this.cart = await cartResponse.json();
        this.render();
        this.updateCartIcon();
      }

    } catch (error) {
      console.error('Error removing free gift:', error);
    } finally {
      this.isRemovingFreeGift = false;
      // Update progress after removal is complete
      this.updateProgress();
    }
  }

  // Handle automatic free gift addition
  async handleFreeGiftAutoAdd() {
    if (!this.autoAddFreeGift || !this.freeGiftProduct || this.freeGiftAdded) return;

    // Check if free gift is already in cart
    const freeGiftItems = this.cart?.items?.filter(item =>
      (item.properties && item.properties._free_gift === 'true') ||
      item.product_handle === this.freeGiftProduct ||
      item.variant_id === parseInt(this.freeGiftProduct)
    );

    // If we already have at least one free gift item, set flag and return
    if (freeGiftItems && freeGiftItems.length > 0) {
      this.freeGiftAdded = true;

      // If we have more than one free gift, remove the extras
      if (freeGiftItems.length > 1) {
        // Keep the first one, remove the rest
        for (let i = 1; i < freeGiftItems.length; i++) {
          const itemKey = freeGiftItems[i].key || freeGiftItems[i].variant_id;
          await this.setQuantity(itemKey, 0);
        }
      }
      return;
    }

    try {
      // Get product info to find variant ID
      const productResponse = await fetch(`/products/${this.freeGiftProduct}.js`);
      const product = await productResponse.json();

      if (product && product.variants && product.variants.length > 0) {
        const variantId = product.variants[0].id;

        // Add free gift with custom properties
        const formData = {
          'items': [{
            'id': variantId,
            'quantity': 1,
            'properties': {
              '_free_gift': 'true',
              '_original_price': product.variants[0].price,
              '_gift_threshold': this.freeGiftThreshold
            }
          }]
        };

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          this.freeGiftAdded = true;

          // Apply discount to make the free gift $0
          await this.applyFreeGiftDiscount(variantId);

          // Update cart to reflect changes
          await this.loadCart();
          await this.refreshCartSection();
        }
      }
    } catch (error) {
      console.error('Error adding free gift:', error);
    } 
  }

  // Apply discount to make free gift $0
  async applyFreeGiftDiscount(variantId) {
    try {
      // Get the current cart to find the free gift item
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const freeGiftItem = cart.items.find(item =>
        item.variant_id === variantId &&
        item.properties &&
        item.properties._free_gift === 'true'
      );

      if (freeGiftItem && freeGiftItem.price > 0) {
        // Update the item with zero price using cart/change.js
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: {
              '_free_gift': 'true',
              '_original_price': freeGiftItem.price,
              '_gift_threshold': this.freeGiftThreshold,
              '_zero_price': 'true'
            }
          })
        });
      }
    } catch (error) {
      console.error('Error applying free gift discount:', error);
    }
  }

  escapeHtml(s) {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  render() {
    const itemsContainer = document.querySelector(this.selectors.items);
    const emptyState = document.querySelector(this.selectors.empty);
    const footer = document.querySelector(this.selectors.footer);
    const subtotal = document.querySelector(this.selectors.subtotal);

    if (!itemsContainer) return;

    // Update item count and total
    if (subtotal) {
      // Display total excluding free gifts
      const totalExcludingGifts = this.getCartTotalExcludingFreeGifts();
      subtotal.textContent = this.priceWithSymbolEnd(totalExcludingGifts);
    }

    // Show/hide empty state and footer
    if (this.cart.item_count === 0) {
      if (emptyState) emptyState.style.display = 'block';
      if (footer) footer.style.display = 'none';
      itemsContainer.innerHTML = '';
      return;
    } else {
      if (emptyState) emptyState.style.display = 'none';
      if (footer) footer.style.display = 'block';
    }

    // Render items
    itemsContainer.innerHTML = this.cart.items.map(item => this.renderItem(item)).join('');

    // Updated by memberr
    // Render discounts
    let discountsContainer = document.querySelector('.bd-quick-cart__discounts')
    if(!discountsContainer) {
      const div = document.createElement("div")
      div.className = "bd-quick-cart__discounts"
      footer.prepend(div)
      discountsContainer = div
    }
    discountsContainer.innerHTML = this.renderDiscounts(this.cart.cart_level_discount_applications)

    // Initialize scroll buttons after rendering
    setTimeout(() => this.updateScrollButtons(), 100);
  }

  priceWithSymbolEnd(cents, symbol) {
    const sym = symbol || window.CURRENCY_SYMBOL || '$';
    const n = Number(cents) / 100;
    return n.toFixed(2).replace('.', ',') + ' ' + sym;
  }

  renderItem(item) {
    const isGift = item.properties && item.properties._free_gift === 'true';
    const compareAt = Number(item._compare_at_price || 0);
    const currentPrice = Number(item.price ?? item._variant_price ?? 0);
    const hasComparePrice = compareAt > currentPrice;
    const discountPct = hasComparePrice
      ? Math.round(((compareAt - currentPrice) / compareAt) * 100)
      : 0;

    return `
      <div class="bd-quick-cart__item ${isGift ? 'bd-quick-cart__item--gift' : ''}" data-item-id="${item.variant_id}">
        <a href="${item.url}" class="bd-quick-cart__item-image">
          ${item.image ? `
            <img 
              src="${item.image}" 
              alt="${item.product_title}"
              width="80"
              height="80"
              loading="lazy"
            >
          ` : ''}
        
          
        
        </a>
        
        <div class="bd-quick-cart__item-details">
          <div class="bd-quick-cart__item-info">
            <h3 class="bd-quick-cart__item-title p-m">
              <a href="${item.url}" class="bd-quick-cart__item-title-link p-m">${item.product_title}</a>
            </h3>
            ${item.variant_title !== 'Default Title' ? `
              <p class="bd-quick-cart__item-variant p-xs">${item.variant_title}</p>
            ` : ''}
            ${isGift ? `
              
            ` : ''}<div class="bd-quick-cart__item-price">
              ${isGift || item.price === 0 ? `
            <div class="bd-quick-cart__badge p-xxs bd-quick-cart__badge--free"
            >
              Gratis
            </div>
          ` : ''}

           ${hasComparePrice ? `
              <div class="bd-quick-cart__badge p-xxs bd-quick-cart__badge--sale">
                ${discountPct}% Sale
              </div>
            ` : ''}

  ${hasComparePrice ? `
    <span class="bd-quick-cart__item-price-compare p-xxs">
      ${this.priceWithSymbolEnd(compareAt)}
    </span>
  ` : ``}

  ${isGift || item.price === 0 ? `
    <span class="bd-quick-cart__item-price-current bd-quick-cart__item-price-current-free">
      ${this.priceWithSymbolEnd(currentPrice)}
    </span>
  ` : `
    <span class="bd-quick-cart__item-price-current">
      ${this.priceWithSymbolEnd(currentPrice)}
    </span>
  `}

</div>
          ${this.renderProductFeatures(item)}
          </div>
          
          
        </div>
        
        <div class="bd-quick-cart__item-actions">
          ${isGift ? `
          <span class="remove-gift" data-item-id="${item.variant_id}">Entfernen</span>
          ` : `
            <div class="bd-quick-cart__quantity">
              <div class="bd-quick-cart__qty-dropdown" data-qty-dropdown data-item-id="${item.variant_id}">
                <button type="button" class="bd-quick-cart__qty-trigger p-xs" data-qty-trigger>
                   <span class="bd-quick-cart__qty-value" data-qty-value>${item.quantity}</span>
                   <div class="bd-quick-cart__qty-icons">
                     <div class="bd-quick-cart__qty-icon"><svg xmlns="http://www.w3.org/2000/svg" width="9" height="4" viewBox="0 0 9 4" fill="none">
  <path d="M0.5 1L4.5 3L8.5 1" stroke="#2E2E2E" stroke-width="0.6" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                     </div>
                   </div>
                 </button>
                <div class="bd-quick-cart__qty-options" data-qty-options>
                  <div class="bd-quick-cart__qty-option" data-qty-option="0">
                    <span class="bd-quick-cart__qty-option-text">0</span>
                  </div>
                  ${Array.from({ length: this.getMaxQuantity() }, (_, i) => i + 1).map(i =>
      `<div class="bd-quick-cart__qty-option${item.quantity === i ? ' bd-quick-cart__qty-option--selected' : ''}" data-qty-option="${i}">
                      <span class="bd-quick-cart__qty-option-text">${i}</span>
                    </div>`
    ).join('')}
                </div>
              </div>
            </div>
          `}
        </div>
      </div>
    `;
  }

  async handleAddToCartAndOpen(button) {
    if (this.isLoading) return;

    this.setLoading(true);

    try {
      // Find the product form
      const form = button.closest('form[data-product-form]');
      if (!form) {
        console.error('Product form not found');
        this.open();
        return;
      }

      const formData = new FormData(form);



      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Update cart and open drawer
      await this.loadCart();
      await this.refreshCartSection();
            this.open();

      // Show success notification
      this.showNotification('Product added to cart!', 'success');

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Error adding product to cart', 'error');
      // Still open cart even if add fails
      this.open();
    } finally {
      this.setLoading(false);
    }
  }

  open() {
    const cartElement = document.querySelector(this.selectors.cart);
    if (cartElement) {
      cartElement.classList.add('is-open');
      this.isOpen = true;
      document.body.style.overflow = 'hidden';

      // Focus management
      const closeBtn = cartElement.querySelector(this.selectors.closeBtn);
      if (closeBtn) {
        closeBtn.focus();
      }
    }
  }

  close() {
    const cartElement = document.querySelector(this.selectors.cart);
    if (cartElement) {
      cartElement.classList.remove('is-open');
      this.isOpen = false;
      document.body.style.overflow = '';

      // Ensure cart icon is updated with current cart state when closing
      this.updateCartIcon();
    }
  }

  setLoading(loading) {
    this.isLoading = loading;
    const cartElement = document.querySelector(this.selectors.cart);
    if (cartElement) {
      cartElement.classList.toggle('bd-quick-cart__loading', loading);
    }
  }

  toggleQuantityDropdown(dropdown) {
    const isOpen = dropdown.classList.contains('is-open');

    // Close all other dropdowns first
    this.closeAllQuantityDropdowns();

    // Toggle current dropdown
    if (!isOpen) {
      dropdown.classList.add('is-open');
    }
  }

  closeAllQuantityDropdowns() {
    const dropdowns = document.querySelectorAll(this.selectors.qtyDropdown);
    dropdowns.forEach(dropdown => {
      dropdown.classList.remove('is-open');
    });
  }

  selectQuantityOption(dropdown, selectedOption, newQty) {
    // Update the display value
    const valueElement = dropdown.querySelector(this.selectors.qtyValue);
    if (valueElement) {
      valueElement.textContent = newQty;
    }

    // Update selected state
    const options = dropdown.querySelectorAll(this.selectors.qtyOption);
    options.forEach(option => {
      option.classList.remove('bd-quick-cart__qty-option--selected');
    });
    selectedOption.classList.add('bd-quick-cart__qty-option--selected');

    // Close dropdown
    dropdown.classList.remove('is-open');
  }

  updateCartIcon() {
    // Calculate total quantity (sum of all item quantities)
    let totalQuantity = 0;
    if (this.cart && this.cart.items) {
      totalQuantity = this.cart.items.reduce((sum, item) => sum + item.quantity, 0);
    }

    // Use setTimeout to ensure our update happens after theme.js cart:updated event
    setTimeout(() => {
      // Update cart icon count in header
      const cartCountElements = document.querySelectorAll('[data-js-cart-count]');

      cartCountElements.forEach(element => {
        element.innerHTML = totalQuantity;
      });

      // Update cart indicator visibility
      const cartIndicators = document.querySelectorAll('[data-js-cart-indicator]');
      cartIndicators.forEach(indicator => {
        if (totalQuantity > 0) {
          indicator.style.display = 'block';
        } else {
          indicator.style.display = 'none';
        }
      });

      // Force a repaint to ensure the update is visible
      cartCountElements.forEach(element => {
        element.offsetHeight; // Trigger reflow
      });
    }, 100); // Small delay to ensure theme.js updates first
  }

  getMaxQuantity() {
    const maxQtyElement = document.querySelector('[data-bd-cart-max-qty]');
    return maxQtyElement ? parseInt(maxQtyElement.dataset.bdCartMaxQty) || 10 : 10;
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);

    // Add space between currency symbol and amount
    return formatted.replace('€', '€ ');
  }

  renderProductFeatures(item) {
    const p = item.properties || {};
    // console.log("BD FEAT ===========");
    // console.log(p);
    const f1 = {
      text: p['_Feature_1_text'],
      icon: p['_Feature_1_icon'],
      alt: p['_Feature_1_icon_alt']
    };
    const f2 = {
      text: p['_Feature_2_text'],
      icon: p['_Feature_2_icon'],
      alt: p['_Feature_2_icon_alt']
    };
    const feats = [f1, f2].filter(f => f && (f.text || f.icon));
    if (!feats.length) return '';

    return `
    <div class="product-feature-list-cart-drawer">
      ${feats.map(f => `
        <div class="product-feature-list__item">
          <div class="product-feature-list__icon">
            ${f.icon ? `
              <img src="${f.icon}" alt="${(f.alt || '').replace(/"/g, '&quot;')}" loading="lazy" width="22" height="22">
            ` : ''}
          </div>
          <div class="product-feature-list__text p-xs">
            ${f.text ? this.escapeHtml(String(f.text)) : ''}
          </div>
        </div>
      `).join('')}
    </div>
  `;
  }



  showNotification(message, type = 'success') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `bd-notification bd-notification--${type}`;
    notification.textContent = message;

    // Style the notification
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '6px',
      backgroundColor: type === 'error' ? '#ff4444' : '#00aa44',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
      zIndex: '10000',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease'
    });

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 10);

    // Remove after delay
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  // Upsell carousel scroll functionality
  scrollUpsells(direction) {
    const track = document.querySelector('[data-upsells-track]');
    if (!track) return;

    const cardWidth = 140 + 12; // card width + gap
    const scrollAmount = cardWidth * 2; // scroll 2 cards at a time

    if (direction === 'prev') {
      track.scrollLeft -= scrollAmount;
    } else {
      track.scrollLeft += scrollAmount;
    }

    // Update scroll button states
    setTimeout(() => this.updateScrollButtons(), 100);
  }

  // Update scroll button visibility and state
  updateScrollButtons() {
    const track = document.querySelector('[data-upsells-track]');
    const prevBtn = document.querySelector('[data-scroll-prev]');
    const nextBtn = document.querySelector('[data-scroll-next]');

    if (!track || !prevBtn || !nextBtn) return;

    const isAtStart = track.scrollLeft <= 0;
    const isAtEnd = track.scrollLeft >= (track.scrollWidth - track.clientWidth - 1);

    prevBtn.disabled = isAtStart;
    nextBtn.disabled = isAtEnd;
  }

  // Initialize touch/swipe support for carousel
  initUpsellCarouselTouch() {
    let startX = 0;
    let scrollLeft = 0;
    let isDown = false;

    document.addEventListener('mousedown', (e) => {
      const track = e.target.closest('[data-upsells-track]');
      if (!track) return;

      isDown = true;
      startX = e.pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
      track.style.cursor = 'grabbing';
    });

    document.addEventListener('mouseleave', () => {
      isDown = false;
      const track = document.querySelector('[data-upsells-track]');
      if (track) track.style.cursor = 'cursor';
    });

    document.addEventListener('mouseup', () => {
      isDown = false;
      const track = document.querySelector('[data-upsells-track]');
      if (track) {
        track.style.cursor = 'cursor';
        this.updateScrollButtons();
      }
    });

    document.addEventListener('mousemove', (e) => {
      const track = e.target.closest('[data-upsells-track]');
      if (!track || !isDown) return;

      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 2;
      track.scrollLeft = scrollLeft - walk;
    });

    // Touch events for mobile
    document.addEventListener('touchstart', (e) => {
      const track = e.target.closest('[data-upsells-track]');
      if (!track) return;

      startX = e.touches[0].pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
    }, { passive: true });

    document.addEventListener('touchmove', (e) => {
      const track = e.target.closest('[data-upsells-track]');
      if (!track) return;

      const x = e.touches[0].pageX - track.offsetLeft;
      const walk = (x - startX) * 2;
      track.scrollLeft = scrollLeft - walk;
    }, { passive: true });

    document.addEventListener('touchend', () => {
      setTimeout(() => this.updateScrollButtons(), 100);
    });

    // Update scroll buttons on scroll
    document.addEventListener('scroll', (e) => {
      if (e.target && typeof e.target.matches === 'function' && e.target.matches('[data-upsells-track]')) {
        this.updateScrollButtons();
      }
    }, true);
  }

  // ADD: ===== Upsell Variant Select helpers =====
  updateUpsellButtonForSelect(selectEl) {
    if (!selectEl) return;

    const card = selectEl.closest('.bd-quick-cart__upsell-card');
    if (!card) return;

    const addBtn = card.querySelector(this.selectors.addUpsell);
    if (!addBtn) return;

    const productTitle = selectEl.getAttribute('data-product-title') || '';
    const option = selectEl.selectedOptions && selectEl.selectedOptions[0];
    if (!option) return;

    const variantId = option.value;
    const variantTitle = option.getAttribute('data-variant-title') || '';
    const variantImage = option.getAttribute('data-variant-image') || '';

    // Update existing button (do NOT replace it)
    addBtn.setAttribute('data-variant-id', variantId);

    // Keep original aria-label prefix, append product + variant
    const baseAria = addBtn.getAttribute('aria-label') || 'Add to cart';
    const prefix = (baseAria.split(' - ')[0] || baseAria).trim();
    const label = variantTitle
      ? `${prefix} - ${productTitle} (${variantTitle})`
      : `${prefix} - ${productTitle}`;
    addBtn.setAttribute('aria-label', label);

    // Disable add button if the variant option is disabled (sold out)
    const isDisabled = option.disabled === true;
    addBtn.toggleAttribute('disabled', isDisabled);
    addBtn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

    // Update the product image if variant image is available
    if (variantImage) {
      const productImage = card.querySelector('.bd-quick-cart__product-image img');
      if (productImage) {
        productImage.src = variantImage;
        productImage.srcset = variantImage;
      }
    }
  }

  initializeVariantSelectDefaults(scope) {
    const root = scope || document;
    const selects = root.querySelectorAll(this.selectors.variantSelect);
    selects.forEach((selectEl) => this.updateUpsellButtonForSelect(selectEl));
  }

  observeUpsellsForVariantSelects() {
    // Observe upsell container for new cards (e.g., drawer re-renders, locale changes)
    const container = document.querySelector('[data-upsells-container]');
    if (!container || this._upsellsObserver) return;

    this._upsellsObserver = new MutationObserver(() => {
      // Re-initialize any new <select data-upsell-variant-select>
      this.initializeVariantSelectDefaults(container);
    });

    this._upsellsObserver.observe(container, { childList: true, subtree: true });
  }

  // Updated by memberr
  // Render discounts 
  renderDiscounts(cartLevelDiscountApplications) {
    return `
    <div class="bd-quick-cart__discounts">
    ${cartLevelDiscountApplications.map((d) => `
      <div class="bd-quick-cart__discount">
          <span class="bd-quick-cart__discount-title">${d.title}</span>
          <span class="bd-quick-cart__discount-amount">-${this.priceWithSymbolEnd(d.total_allocated_amount)}</span>
      </div>
      `)}
    </div>
    `
  }
}

// Initialize BD Quick Cart
document.addEventListener('DOMContentLoaded', () => {
  window.bdQuickCart = new BDQuickCart();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BDQuickCart;
}